import { formatDocumentInstant } from '../format.js';
import { html } from '../html.js';
import { t } from '../strings.js';
import { docDate, field, fields, footer, header, ltr } from './parts.js';
/**
 * DSH-10's Print and PDF, for all four report centres at once.
 *
 * ONE template, not four, because DSH-10 is a skeleton: every centre draws
 * band 1 (period), band 3 (four summary cards) and band 5 (the detail table).
 * What differs between DSH-05 and DSH-07 is which columns and which cards —
 * data, not structure. So the caller hands over already-labelled cards and an
 * already-built column/row grid, in the reader's language, and this file lays
 * them out.
 *
 * BR-DSH-10-24 — "the export carries exactly the columns/rows on screen" — is
 * therefore satisfied by construction rather than by care: the screen and the
 * document are given the same `columns`/`rows` arrays.
 *
 * NFR-35 — "a chart with no measuring axis must print its own totals in
 * words, because a printed bar cannot be measured." The report print path
 * satisfies this by printing NO chart at all: what lands on paper is the
 * labelled summary cards and the detail table — every figure as text. (The
 * on-screen charts are aria-hidden canvases whose data these bands carry.)
 *
 * Values arrive PRE-FORMATTED as strings. A report cell can be money, a count,
 * a percentage or a date depending on the centre, and a template that tried to
 * guess would guess wrong; `numericColumns` says only which ones should be
 * right-aligned LTR islands.
 */
export function reportHtml(data, locale, company, generatedAt, generatedBy) {
    const numeric = new Set(data.numericColumns ?? []);
    return html `
    ${header(company, data.title, null)}
    ${ /*
      BR-DSH-10-16 — "Every report must print a heading block: the company
      name, the report name, the window covered, the filters applied, the run
      time, and the name of the user who ran it." The first two are the
      header() above; the rest are this one <dl>, in the rule's own order.
      The run time and the person appear HERE and not only in the page footer,
      because the rule's noun is a heading block: the sheet an auditor is
      handed states what was asked, of what window, under which filters, by
      whom and when, before a single figure — and DSH-10 Band 6 adds that the
      block "must name the configuration if one is in use, alongside the
      filters it applied", which is the configName row. Filters arrive
      already labelled in the reader's language (see ReportData); a report
      with none in force (DSH-08 has no More filters band) simply has no
      filter rows, and an anonymous artefact (a test) has no Run-by row
      rather than an empty one.
    */''}
    ${fields([
        // THE WINDOW ROWS ARE ABSENT ON AN AS-OF-NOW REPORT, not blank.
        //
        // DSH-07 is exempt from BR-DSH-10-02 under delegated ruling A14 and
        // sends no window (see ReportGridSchema). BR-DSH-10-16 still wants "the
        // window covered" in the heading block, and for a point-in-time report
        // that is the Run-at row below — the as-of moment is the only window it
        // has. A From row showing an em dash would claim there IS a period and
        // that it could not be determined, which is the one reading that is
        // false; omitting the row says the report is not about a period at all.
        ...(data.from === undefined || data.to === undefined
            ? []
            : [field(t('from', locale), docDate(data.from)), field(t('to', locale), docDate(data.to))]),
        ...(data.filters ?? []).map((filter) => field(filter.label, filter.value)),
        ...(data.configName ? [field(t('configuration', locale), data.configName)] : []),
        field(t('runAt', locale), ltr(formatDocumentInstant(generatedAt))),
        ...(generatedBy ? [field(t('runBy', locale), generatedBy)] : []),
    ])}

    ${data.cards.length === 0
        ? null
        : html `
          <section class="doc-cards">
            ${data.cards.map((card) => html `
                <div class="doc-card">
                  <div class="label">${card.label}</div>
                  <div class="value">${ltr(card.value)}</div>
                  ${card.changePct === null || card.changePct === undefined
            ? null
            : html `<div class="delta">${ltr(`${card.changePct}%`)} ${t('vsPrevious', locale)}</div>`}
                </div>
              `)}
          </section>
        `}

    <table class="doc-table">
      <thead>
        <tr>
          ${data.columns.map((column, index) => html `<th class="${numeric.has(index) ? 'amount' : ''}">${column}</th>`)}
        </tr>
      </thead>
      <tbody>
        ${data.rows.length === 0
        ? html `<tr><td class="doc-empty" colspan="${String(Math.max(1, data.columns.length))}">${t('noRows', locale)}</td></tr>`
        : data.rows.map((row) => html `
                <tr>
                  ${data.columns.map((_, index) => {
            const cell = row[index] ?? '';
            return numeric.has(index)
                ? html `<td class="amount">${ltr(cell)}</td>`
                : html `<td>${cell}</td>`;
        })}
                </tr>
              `)}
      </tbody>
    </table>
    ${footer(locale, generatedAt, company.name, generatedBy)}
  `;
}
//# sourceMappingURL=report.js.map